import { useState } from 'react';
import { toast } from 'sonner';
import { trackProductView } from '@/lib/analytics/trackEvent';
import {
  getProductChoiceGroups,
  getRegularAddOns,
  mergeAddOnsWithChoices,
  toggleChoiceSelection,
} from '@/lib/productChoiceGroups';

export function useCatalogProductCustomization({
  businessId,
  isFallbackMode,
  addToCart,
  onProductAdded,
}) {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [choiceSelections, setChoiceSelections] = useState({});
  const [removedIngredients, setRemovedIngredients] = useState([]);
  const [quantity, setQuantity] = useState(1);

  const openProductModal = (product) => {
    setSelectedProduct(product);
    setSelectedAddOns([]);
    setChoiceSelections({});
    setRemovedIngredients([]);
    setQuantity(1);
    setProductModalOpen(true);
    if (product?.id && businessId) {
      trackProductView(product.id, businessId);
    }
  };

  const updateAddOnQuantity = (addon, delta) => {
    setSelectedAddOns((prev) => {
      const exists = prev.find((a) => a.name === addon.name);
      if (!exists && delta > 0) {
        return [...prev, { name: addon.name, price: Number(addon.price) || 0, quantity: 1 }];
      }
      if (exists) {
        const newQuantity = (Number(exists.quantity) || 1) + delta;
        if (newQuantity <= 0) {
          return prev.filter((a) => a.name !== addon.name);
        }
        return prev.map((a) =>
          a.name === addon.name ? { ...a, quantity: newQuantity } : a,
        );
      }
      return prev;
    });
  };

  const getAddOnQuantity = (addon) => {
    const exists = selectedAddOns.find((a) => a.name === addon.name);
    return exists?.quantity || 0;
  };

  const toggleChoice = (group, optionName) => {
    setChoiceSelections((prev) => {
      const next = toggleChoiceSelection(prev, group, optionName);
      if (next === prev && group.max_selections) {
        toast.message(`Máximo ${group.max_selections} selecciones en ${group.name}`);
      }
      return next;
    });
  };

  const toggleIngredient = (ingredient) => {
    setRemovedIngredients((prev) =>
      prev.includes(ingredient) ? prev.filter((i) => i !== ingredient) : [...prev, ingredient],
    );
  };

  const buildLineAddOns = () => {
    if (!selectedProduct) return [];
    return mergeAddOnsWithChoices(selectedProduct, choiceSelections, selectedAddOns);
  };

  const calculatePrice = () => {
    if (!selectedProduct) return 0;
    let price = Number(selectedProduct.final_price) || Number(selectedProduct.price) || 0;
    buildLineAddOns().forEach((addon) => {
      const addonQty = Number(addon.quantity) || 1;
      const addonPrice = Number(addon.price) || 0;
      price += addonPrice * addonQty;
    });
    return price * quantity;
  };

  const handleAddToCart = () => {
    if (!selectedProduct) return;
    const groups = getProductChoiceGroups(selectedProduct);
    if (groups.length > 0) {
      const totalSelected = groups.reduce(
        (n, g) => n + (choiceSelections[g.name] || []).length,
        0,
      );
      if (totalSelected === 0) {
        toast.error('Elegí al menos una opción');
        return;
      }
    }
    if (isFallbackMode) {
      toast.error('Pedidos online temporalmente pausados. Puedes revisar el menú.');
      return;
    }
    addToCart(selectedProduct, buildLineAddOns(), removedIngredients, quantity, { businessId });
    setProductModalOpen(false);
    onProductAdded?.(selectedProduct);
  };

  const regularAddOns = selectedProduct ? getRegularAddOns(selectedProduct) : [];

  return {
    selectedProduct,
    productModalOpen,
    setProductModalOpen,
    selectedAddOns,
    choiceSelections,
    removedIngredients,
    quantity,
    setQuantity,
    openProductModal,
    updateAddOnQuantity,
    getAddOnQuantity,
    toggleChoice,
    toggleIngredient,
    calculatePrice,
    handleAddToCart,
    regularAddOns,
  };
}
